import { Fragment, useMemo, useState } from "react";
import { Dialog, Listbox, Popover, Transition } from "@headlessui/react";
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  FilterIcon,
  LocationMarkerIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  XCircleIcon,
  XIcon,
} from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import Loader from "../../../components/loader/Loader";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const STATUS_STYLES = {
  U: { label: "Submitted", className: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  A: { label: "Received", className: "bg-blue-50 text-blue-700 ring-blue-200" },
  S: { label: "Assigned", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  W: { label: "In Progress", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  R: { label: "Review", className: "bg-purple-50 text-purple-700 ring-purple-200" },
  C: { label: "Completed", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  I: { label: "Invoiced", className: "bg-sky-50 text-sky-700 ring-sky-200" },
  T: { label: "Canceled", className: "bg-slate-100 text-slate-700 ring-slate-200" },
};

const TAG_STYLES = {
  red: "bg-red-50 text-red-700 ring-red-200",
  orange: "bg-orange-50 text-orange-700 ring-orange-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  pink: "bg-pink-50 text-pink-700 ring-pink-200",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  lime: "bg-lime-50 text-lime-700 ring-lime-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
};

function getCustomer(job) {
  return job.integration_customer || job.customer || {};
}

function getInitials(value = "") {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "—";
}

function getServiceNames(job) {
  const regular = (job.job_service_assignments || []).map(
    (assignment) => assignment.service_name || assignment.service?.name || assignment.name,
  );
  const retainer = (job.job_retainer_service_assignments || []).map(
    (assignment) => assignment.service_name || assignment.retainer_service?.name || assignment.name,
  );
  const categories = (job.categories || []).map(
    (category) => category.customer_category?.name || category.name,
  );
  const names = [...regular, ...retainer].filter(Boolean);
  return names.length ? names : categories.filter(Boolean);
}

function getAssigneeNames(job) {
  const direct = (job.asignees || []).map((person) =>
    [person.first_name, person.last_name].filter(Boolean).join(" ") || person.username,
  );
  if (direct.length) return direct.filter(Boolean);

  return [...(job.job_service_assignments || []), ...(job.job_retainer_service_assignments || [])]
    .map((assignment) => {
      const person = assignment.project_manager;
      if (!person) return null;
      if (typeof person === "string") return person;
      return [person.first_name, person.last_name].filter(Boolean).join(" ") || person.username;
    })
    .filter(Boolean)
    .filter((name, index, names) => names.indexOf(name) === index);
}

function StatusBadge({ status }) {
  const config = STATUS_STYLES[status] || {
    label: status || "Unknown",
    className: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span className={classNames(
      "inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ring-inset",
      config.className,
    )}>
      {config.label}
    </span>
  );
}

function JobTags({ tags = [] }) {
  if (!tags.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <span
          key={tag.id || tag.tag_short_name || tag.name}
          className={classNames(
            "inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
            TAG_STYLES[tag.tag_color] || TAG_STYLES.slate,
          )}
        >
          {tag.tag_short_name || tag.name}
        </span>
      ))}
    </div>
  );
}

function MessageSquareTextIcon({ className }) {
  return (
    <svg
      data-testid="unread-comments-icon"
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
      <path d="M7 11h10" />
      <path d="M7 15h6" />
      <path d="M7 7h8" />
    </svg>
  );
}

function UnreadCommentsBadge({ count }) {
  if (!count) return null;

  return (
    <span
      className="inline-flex flex-none items-center gap-1 rounded-full bg-[#E3222A] px-2 py-1 text-xs font-bold text-white"
      aria-label={`${count} unread comments`}
    >
      <MessageSquareTextIcon className="h-3 w-3" />
      <span aria-hidden="true">{count}</span>
    </span>
  );
}

function CustomerAvatar({ customer }) {
  const imageUrl = customer.logo || customer.avatar || customer.profile?.avatar;
  const [failedImageUrl, setFailedImageUrl] = useState(null);
  const showImage = imageUrl && failedImageUrl !== imageUrl;

  return (
    <span className="inline-flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-full bg-[#18243A] text-xs font-bold text-white ring-1 ring-slate-200">
      {showImage ? (
        <img
          src={imageUrl}
          alt={`${customer.name} profile`}
          className="h-full w-full object-cover"
          onError={() => setFailedImageUrl(imageUrl)}
        />
      ) : (
        getInitials(customer.name)
      )}
    </span>
  );
}

function Timing({ job, mobile = false }) {
  const completeLabel = job.status === "C" || job.status === "I" ? "Completed" : "Complete by";
  const completeValue = job.status === "C" || job.status === "I"
    ? job.completion_date
    : job.complete_before_formatted_date || job.completeBy;

  return (
    <div className={classNames(mobile && "rounded-lg bg-slate-50 px-3 py-2.5")}>
      {mobile && <p className="mb-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Timing</p>}
      <dl className="space-y-1.5 text-base">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-slate-400">Arrival</dt>
          <dd className="text-right font-medium text-slate-800">
            {job.on_site ? "On site" : job.arrival_formatted_date || "TBD"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-slate-400">Departure</dt>
          <dd className="text-right font-medium text-slate-800">{job.departure_formatted_date || "TBD"}</dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-slate-400">{completeLabel}</dt>
          <dd className="text-right font-semibold text-slate-900">{completeValue || "TBD"}</dd>
        </div>
      </dl>
      <div className="mt-2 flex justify-end">
        {job.isDueToday && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-500" />Due today
          </span>
        )}
        {job.isOverdue && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600">
            <span className="h-2 w-2 rounded-full bg-red-500" />Overdue
          </span>
        )}
      </div>
    </div>
  );
}

function ServiceSummary({ job, showOwner }) {
  const services = getServiceNames(job);
  const assignees = getAssigneeNames(job);
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
        {services.length === 1 ? "Service" : "Services"}
      </p>
      <div className="relative mt-1 flex items-center gap-1.5 text-base font-semibold text-slate-800">
        <span className="min-w-0 truncate">{services[0] || "No service specified"}</span>
        {services.length > 1 && (
          <Popover className="pointer-events-auto static flex-none md:relative">
            {({ open }) => (
              <>
                <Popover.Button
                  className="flex-none rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-bold text-sky-700 shadow-sm hover:border-sky-300 hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  aria-label={`Show all ${services.length} services`}
                >
                  +{services.length - 1} more
                </Popover.Button>
                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="translate-y-1 scale-95 opacity-0"
                  enterTo="translate-y-0 scale-100 opacity-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="translate-y-0 scale-100 opacity-100"
                  leaveTo="translate-y-1 scale-95 opacity-0"
                >
                  <Popover.Panel
                    static
                    data-testid="services-popover"
                    className="absolute inset-x-0 top-full z-50 mt-2 w-auto origin-top rounded-lg bg-white p-3 text-left shadow-lg ring-1 ring-slate-900/10 focus:outline-none md:left-0 md:right-auto md:w-72 md:origin-top-left"
                  >
                    <h3 className="text-sm font-extrabold text-slate-900">{services.length} services</h3>
                    <ul className="mt-2 space-y-2">
                      {services.map((service, index) => (
                        <li key={`${service}-${index}`} className="flex items-start gap-2 text-sm font-normal text-slate-700">
                          <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[#E3222A]" />
                          <span>{service}</span>
                        </li>
                      ))}
                    </ul>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        )}
      </div>
      {showOwner && (
        <div className="mt-3">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Project manager</p>
          <p className={classNames("mt-0.5 text-base font-medium", assignees.length ? "text-slate-800" : "text-red-600")}>
            {assignees.join(", ") || "Unassigned"}
          </p>
        </div>
      )}
      <JobTags tags={job.tags} />
    </div>
  );
}

function LocationSummary({ job, showCustomer }) {
  const customer = getCustomer(job);
  return (
    <div className="min-w-0">
      {showCustomer && customer.name && (
        <div className="mt-2 flex items-center gap-2">
          <CustomerAvatar customer={customer} />
          <p className="truncate text-base font-medium text-slate-800">{customer.name}</p>
        </div>
      )}
      <p className={classNames("flex min-w-0 items-center gap-1.5 text-base text-slate-600", showCustomer ? "mt-2" : "mt-3")}>
        <LocationMarkerIcon className="h-5 w-5 flex-none text-slate-400" />
        <strong className="text-slate-800">{job.airport?.initials || "—"}</strong>
        <span className="truncate">· {job.fbo?.name || "FBO not specified"}</span>
      </p>
      <p className="mt-1 flex min-w-0 items-center gap-1.5 text-sm text-slate-500">
        <span className="flex h-5 w-5 flex-none items-center justify-center text-base text-slate-400">✈</span>
        <span className="truncate">{job.aircraftType?.name || "Aircraft not specified"}</span>
      </p>
    </div>
  );
}

function QueueJob({ job, vendorView }) {
  return (
    <li data-testid="queue-job" className="relative z-0 focus-within:z-40">
      <article className="relative rounded-xl border border-slate-200 bg-white shadow-sm transition hover:bg-slate-50 md:rounded-none md:border-0 md:shadow-none">
        <Link
          to={`/jobs/${job.id}/details`}
          className="absolute inset-0 z-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#E3222A] md:rounded-none"
          aria-label={`Open ${job.tailNumber} job details`}
        />
        <div className="pointer-events-none relative z-10 px-4 py-4 sm:px-5">
          <div data-testid="queue-job-desktop" className="hidden grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(245px,.75fr)_24px] items-center gap-5 md:grid">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-slate-950">{job.tailNumber}</span>
                <span className="truncate text-sm text-slate-400">{job.purchase_order}</span>
              </div>
              <LocationSummary job={job} showCustomer={!vendorView} />
            </div>
            <ServiceSummary job={job} showOwner={!vendorView} />
            <div className="min-w-0">
              <div className="mb-3 flex items-center justify-end gap-1.5">
                <StatusBadge status={job.status} />
                <UnreadCommentsBadge count={job.comments_count} />
              </div>
              <Timing job={job} />
            </div>
            <ChevronRightIcon className="h-6 w-6 text-slate-300" />
          </div>

          <div data-testid="queue-job-mobile" className="md:hidden">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-slate-950">{job.tailNumber}</span>
                  <span className="truncate text-sm text-slate-400">{job.purchase_order}</span>
                </div>
              </div>
              <div className="flex flex-none items-center gap-1.5">
                <StatusBadge status={job.status} />
                <UnreadCommentsBadge count={job.comments_count} />
              </div>
            </div>
            <LocationSummary job={job} showCustomer={!vendorView} />
            <div className="mt-3"><Timing job={job} mobile /></div>
            <div className="mt-3 rounded-lg bg-slate-50/70 px-3 py-2.5">
              <ServiceSummary job={job} showOwner={!vendorView} />
            </div>
          </div>
        </div>
      </article>
    </li>
  );
}

function QueueList({ jobs, vendorView }) {
  return (
    <section className="bg-transparent md:rounded-xl md:border md:border-slate-200 md:bg-white md:shadow-sm" aria-label="Jobs queue results">
      <div className="hidden grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(245px,.75fr)_24px] gap-5 rounded-t-xl border-b border-slate-200 bg-slate-50 px-5 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-slate-400 md:grid">
        <span>Job &amp; location</span>
        <span>{vendorView ? "Services" : "Service & owner"}</span>
        <span className="text-right">Status &amp; timing</span>
        <span aria-hidden="true" />
      </div>
      <ul className="space-y-3 md:divide-y md:divide-slate-200 md:space-y-0">
        {jobs.map((job) => <QueueJob key={job.id} job={job} vendorView={vendorView} />)}
      </ul>
    </section>
  );
}

function MetricButton({ value, label, detail, tone = "default", onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-[88px] min-w-0 px-4 py-3.5 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#E3222A]"
    >
      <span className={classNames(
        "block text-3xl font-bold",
        tone === "warning" ? "text-amber-700" : tone === "danger" ? "text-red-600" : "text-slate-950",
      )}>{value}</span>
      <span className="mt-0.5 block text-sm font-medium text-slate-600">{label}</span>
      {detail && <span className="mt-0.5 block text-xs font-semibold text-amber-700">{detail}</span>}
    </button>
  );
}

function QueueSnapshot({ metrics, unassignedCount, onFiltersOpen, dueToday, overdue }) {
  const [expanded, setExpanded] = useState(false);
  const primaryMetrics = [
    { ...metrics.find((metric) => metric.label === "Due today"), icon: ClockIcon, selected: dueToday },
    { ...metrics.find((metric) => metric.label === "Overdue"), icon: ClockIcon, selected: overdue },
    { label: "Unassigned", value: unassignedCount, tone: "warning", icon: UserIcon, onClick: onFiltersOpen },
  ];
  const moreMetrics = metrics.filter((metric) => !["Due today", "Overdue"].includes(metric.label));

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" aria-label="Queue snapshot">
      <div className="flex items-center gap-1 overflow-x-auto p-1.5 sm:flex-wrap sm:gap-2 sm:p-2">
        {primaryMetrics.map(({ label, value, tone, icon: Icon, onClick, selected }) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={classNames(
              "inline-flex min-h-[32px] shrink-0 items-center gap-0.5 whitespace-nowrap rounded-md border px-1 py-1.5 text-[11px] font-medium transition sm:min-h-[36px] sm:gap-2 sm:px-3 sm:py-2 sm:text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E3222A]",
              value > 0 && tone === "warning" ? "border-amber-100 text-amber-700 hover:bg-amber-50" :
                value > 0 && tone === "danger" ? "border-red-100 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-50",
              selected && "bg-slate-100 ring-1 ring-inset ring-slate-300",
            )}
          >
            <Icon className="h-3 w-3 flex-none sm:h-4 sm:w-4" aria-hidden="true" />
            <span>{label}</span><span>{value}</span>
          </button>
        ))}
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="queue-more-stats"
          aria-label={expanded ? "Less stats" : "More stats"}
          onClick={() => setExpanded((value) => !value)}
          className="ml-auto inline-flex min-h-[32px] shrink-0 items-center justify-center gap-1 rounded-md border border-slate-200 px-1 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:min-h-[36px] sm:border-transparent sm:px-2 sm:py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E3222A]"
        >
          <span className="hidden sm:inline">{expanded ? "Less stats" : "More stats"}</span>
          <ChevronDownIcon className={classNames("h-4 w-4", expanded && "rotate-180")} aria-hidden="true" />
        </button>
      </div>
      <div id="queue-more-stats" hidden={!expanded}>
        <div data-testid="queue-snapshot-metrics" className="grid grid-cols-2 divide-x divide-y divide-slate-100 border-t border-slate-100 sm:grid-cols-5 sm:divide-y-0">
          {moreMetrics.map((metric) => <MetricButton key={metric.label} {...metric} />)}
        </div>
      </div>
    </section>
  );
}

function SearchField({ placeholder, value, onChange }) {
  return (
    <label className="relative block">
      <span className="sr-only">{placeholder}</span>
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-lg border-slate-200 py-2 pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-red-400 focus:ring-red-400"
      />
    </label>
  );
}

function FilterSection({ title, detail, children }) {
  return (
    <section className="border-b border-slate-200 px-5 py-5 last:border-b-0">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-slate-700">{title}</h3>
        {detail && <span className="text-xs font-medium text-slate-400">{detail}</span>}
      </div>
      {children}
    </section>
  );
}

function CheckboxRow({ checked, label, count, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50">
      <span className={classNames(
        "flex h-5 w-5 flex-none items-center justify-center rounded border",
        checked ? "border-[#E3222A] bg-[#E3222A] text-white" : "border-slate-300 bg-white text-transparent",
      )}>
        <CheckIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700">{label}</span>
      {count != null && <span className="text-xs tabular-nums text-slate-400">{count}</span>}
    </button>
  );
}

function FilterDrawer({
  open,
  onClose,
  activeFilterCount,
  availableStatuses,
  statusSelected,
  onToggleStatus,
  dueToday,
  dueTodayCount,
  onToggleDueToday,
  overdue,
  overdueCount,
  onToggleOverdue,
  tags,
  onToggleTag,
  airportTypes,
  airportTypeSelected,
  onToggleAirportType,
  customers,
  customerSelected,
  onSelectCustomer,
  projectManagers,
  projectManagerSelected,
  onSelectProjectManager,
  airports,
  airportSelected,
  onSelectAirport,
  vendors,
  vendorSelected,
  onSelectVendor,
  onClearAll,
  resultCount,
  showVendors,
}) {
  const [customerSearch, setCustomerSearch] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [airportSearch, setAirportSearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const filterItems = (items, term, getLabel) => items.filter((item) =>
    item.id !== "All" && getLabel(item).toLowerCase().includes(term.toLowerCase()),
  );
  const filteredCustomers = filterItems(customers, customerSearch, (item) => item.name || "");
  const filteredAssignees = filterItems(projectManagers, assigneeSearch, (item) =>
    [item.first_name, item.last_name].filter(Boolean).join(" ") || item.username || "",
  );
  const filteredAirports = filterItems(airports, airportSearch, (item) => item.name || item.initials || "");
  const filteredVendors = filterItems(vendors, vendorSearch, (item) => item.name || "");

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-slate-950/30 backdrop-blur-[1px]" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-6 sm:pl-10">
              <Transition.Child as={Fragment} enter="transform transition ease-in-out duration-200" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transform transition ease-in-out duration-200" leaveFrom="translate-x-0" leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-[420px] flex-col bg-white shadow-2xl">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Dialog.Title className="text-lg font-bold text-slate-950">Filters</Dialog.Title>
                        <Dialog.Description className="mt-0.5 text-sm text-slate-500">Narrow the queue, then apply your selections.</Dialog.Description>
                      </div>
                      <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close filters">
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <FilterSection title="Status" detail={statusSelected.id === "All" ? null : "1 selected"}>
                      <div className="space-y-0.5">
                        {availableStatuses.map((status) => (
                          <CheckboxRow
                            key={status.id}
                            checked={statusSelected.id === status.id}
                            label={status.name}
                            count={status.id === "All" ? resultCount : null}
                            onClick={() => onToggleStatus(status)}
                          />
                        ))}
                      </div>
                    </FilterSection>
                    <FilterSection title="Alerts">
                      <CheckboxRow checked={dueToday} label="Due today" count={dueTodayCount} onClick={onToggleDueToday} />
                      <CheckboxRow checked={overdue} label="Overdue" count={overdueCount} onClick={onToggleOverdue} />
                    </FilterSection>
                    <FilterSection title="Tags">
                      <div className="space-y-0.5">
                        {tags.map((tag) => <CheckboxRow key={tag.id} checked={Boolean(tag.selected)} label={tag.name} onClick={() => onToggleTag(tag)} />)}
                      </div>
                    </FilterSection>
                    <FilterSection title="Airport types">
                      <div className="space-y-0.5">
                        {airportTypes.map((type) => <CheckboxRow key={type.id} checked={airportTypeSelected.id === type.id} label={type.name} onClick={() => onToggleAirportType(type)} />)}
                      </div>
                    </FilterSection>
                    <FilterSection title="Customers" detail={`${customers.length} in queue`}>
                      <SearchField placeholder="Search customers" value={customerSearch} onChange={setCustomerSearch} />
                      <div className="mt-2 space-y-0.5">
                        {filteredCustomers.map((customer) => <CheckboxRow key={customer.id} checked={customerSelected.id === customer.id} label={customer.name} onClick={() => onSelectCustomer(customer)} />)}
                      </div>
                    </FilterSection>
                    <FilterSection title="Assignees" detail={`${projectManagers.length} in queue`}>
                      <SearchField placeholder="Search assignees" value={assigneeSearch} onChange={setAssigneeSearch} />
                      <div className="mt-2 space-y-0.5">
                        {filteredAssignees.map((person) => {
                          const label = [person.first_name, person.last_name].filter(Boolean).join(" ") || person.username;
                          return <CheckboxRow key={person.id} checked={projectManagerSelected.id === person.id} label={label} onClick={() => onSelectProjectManager({ ...person, name: label })} />;
                        })}
                      </div>
                    </FilterSection>
                    <FilterSection title="Airports" detail={`${Math.max(airports.length - 1, 0)} in queue`}>
                      <SearchField placeholder="Search airports" value={airportSearch} onChange={setAirportSearch} />
                      <div className="mt-2 space-y-0.5">
                        {filteredAirports.map((airport) => <CheckboxRow key={airport.id} checked={airportSelected.id === airport.id} label={airport.name} onClick={() => onSelectAirport(airport)} />)}
                      </div>
                    </FilterSection>
                    {showVendors && (
                      <FilterSection title="Vendors" detail={`${Math.max(vendors.length - 1, 0)} in queue`}>
                        <SearchField placeholder="Search vendors" value={vendorSearch} onChange={setVendorSearch} />
                        <div className="mt-2 space-y-0.5">
                          {filteredVendors.map((vendor) => <CheckboxRow key={vendor.id} checked={vendorSelected.id === vendor.id} label={vendor.name} onClick={() => onSelectVendor(vendor)} />)}
                        </div>
                      </FilterSection>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-5 py-4">
                    <button type="button" onClick={onClearAll} className="text-sm font-semibold text-slate-600 hover:text-slate-950">Clear all</button>
                    <button type="button" onClick={onClose} className="rounded-lg bg-[#E3222A] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#c91d24]">
                      Show {resultCount} jobs
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function EmptyState({ canCreate }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
      <h2 className="text-sm font-bold text-slate-900">No jobs found</h2>
      <p className="mt-1 text-xs text-slate-500">Try adjusting the queue filters{canCreate ? " or create a new job" : ""}.</p>
      {canCreate && (
        <Link to="/create-job" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#E3222A] px-4 py-2 text-sm font-semibold text-white">
          <PlusIcon className="h-5 w-5" />New job
        </Link>
      )}
    </div>
  );
}

export default function JobsQueueRedesign({
  currentUser,
  jobs,
  loading,
  totalJobs,
  currentPage,
  onPageChange,
  vendorInsuranceData,
  onDismissVendorInsurance,
  searchText,
  onSearchChange,
  onSearchKeyDown,
  onRefresh,
  sortOptions,
  sortSelected,
  onSortChange,
  filtersOpen,
  onFiltersOpen,
  onFiltersClose,
  activeFilters,
  onRemoveActiveFilter,
  onClearAll,
  availableStatuses,
  statusSelected,
  onToggleStatus,
  dueToday,
  dueTodayCount,
  onToggleDueToday,
  overdue,
  overdueCount,
  onToggleOverdue,
  tags,
  onToggleTag,
  airportTypes,
  airportTypeSelected,
  onToggleAirportType,
  customers,
  customerSelected,
  onSelectCustomer,
  projectManagers,
  projectManagerSelected,
  onSelectProjectManager,
  airports,
  airportSelected,
  onSelectAirport,
  vendors,
  vendorSelected,
  onSelectVendor,
}) {
  const internalView = Boolean(
    currentUser.isAdmin ||
    currentUser.isSuperUser ||
    currentUser.isAccountManager ||
    currentUser.isInternalCoordinator,
  );
  const vendorView = !internalView && Boolean(
    currentUser.isProjectManager ||
    currentUser.isExternalProjectManager ||
    currentUser.isMasterPM,
  );
  const canCreate = internalView;
  const showVendorFilter = Boolean(currentUser.isAdmin || currentUser.isSuperUser || currentUser.isAccountManager);
  const unassignedCount = useMemo(() => jobs.filter((job) => getAssigneeNames(job).length === 0).length, [jobs]);
  const activeFilterCount = activeFilters.length + tags.filter((tag) => tag.selected).length + (dueToday ? 1 : 0) + (overdue ? 1 : 0);
  const visibleTotal = dueToday || overdue ? jobs.length : totalJobs;
  const metrics = [
    { value: totalJobs, label: "Open jobs", onClick: onClearAll },
    { value: dueTodayCount, label: "Due today", tone: "warning", onClick: onToggleDueToday },
    { value: overdueCount, label: "Overdue", tone: "danger", onClick: onToggleOverdue },
    { value: Math.max(airports.length - 1, 0), label: "Airports", onClick: onFiltersOpen },
    { value: customers.length, label: "Customers", onClick: onFiltersOpen },
    { value: projectManagers.length, label: "Assignees", detail: unassignedCount ? `${unassignedCount} unassigned` : null, onClick: onFiltersOpen },
    { value: Math.max(vendors.length - 1, 0), label: "Vendors", onClick: onFiltersOpen },
  ];

  return (
    <div className="min-h-screen bg-[#F4F6F8] pb-16 text-[#18243A]">
      <div className="mx-auto w-full max-w-[1500px] px-4 pt-6 sm:px-6 lg:px-8 lg:pt-8">
        {vendorInsuranceData && vendorView && currentUser.isExternalProjectManager && vendorInsuranceData.is_vendor_external && (
          !vendorInsuranceData.is_vendor_w9_uploaded ||
          !vendorInsuranceData.is_vendor_insurance_uploaded ||
          vendorInsuranceData.is_vendor_insurance_expired ||
          vendorInsuranceData.is_vendor_insurance_about_to_expire
        ) && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex gap-3">
              <ExclamationCircleIcon className="h-5 w-5 flex-none text-red-500" />
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-red-900">Important information</h2>
                <p className="mt-1 text-xs text-red-700">Your vendor documentation needs attention. Manage your files from the Vendor Files page.</p>
                <Link to="/vendor-files" className="mt-2 inline-block text-xs font-semibold text-red-800 underline">Manage files</Link>
              </div>
              <button type="button" onClick={onDismissVendorInsurance} className="self-start rounded p-1 text-red-500 hover:bg-red-100" aria-label="Dismiss vendor notice"><XCircleIcon className="h-5 w-5" /></button>
            </div>
          </div>
        )}

        <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-slate-950">Jobs Queue</h1>
              <span className="rounded-full bg-[#18243A] px-2.5 py-1 text-xs font-semibold text-white">
                {visibleTotal} {vendorView ? "assigned" : "open"}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {vendorView ? "Work currently assigned to your team" : "Prioritized operational work across all locations"}
            </p>
          </div>
          {canCreate && (
            <div className="flex flex-none items-center gap-2">
              <Link to="/create-schedule" className="hidden items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 sm:inline-flex">
                <CalendarIcon className="h-5 w-5" />Schedule job
              </Link>
              <Link to="/create-job" className="inline-flex items-center gap-2 rounded-lg bg-[#E3222A] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#c91d24]">
                <PlusIcon className="h-5 w-5" />New job
              </Link>
            </div>
          )}
        </header>

        {!vendorView && (
          <>
            <div className="mt-5"><QueueSnapshot metrics={metrics} unassignedCount={unassignedCount} onFiltersOpen={onFiltersOpen} dueToday={dueToday} overdue={overdue} /></div>
            <section className="mt-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" aria-label="Queue controls">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-[minmax(260px,1fr)_auto_auto_auto]">
                <label className="relative col-span-2 block md:col-span-1">
                  <span className="sr-only">Search jobs</span>
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={searchText}
                    onChange={(event) => onSearchChange(event.target.value)}
                    onKeyDown={onSearchKeyDown}
                    placeholder="Search by tail number or P.O"
                    className="h-11 w-full rounded-lg border-slate-200 py-2 pl-10 pr-3 text-sm focus:border-red-400 focus:ring-red-400"
                  />
                </label>
                <Listbox value={sortSelected} onChange={onSortChange}>
                  <div className="relative">
                    <Listbox.Button className="flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 md:min-w-[155px]">
                      <span className="flex min-w-0 items-center gap-2"><CalendarIcon className="h-5 w-5 flex-none" /><span className="truncate">{sortSelected.name}</span></span>
                      <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                    </Listbox.Button>
                    <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
                      <Listbox.Options className="absolute right-0 z-30 mt-1 w-48 rounded-lg border border-slate-200 bg-white p-1 text-sm shadow-xl focus:outline-none">
                        {sortOptions.map((option) => (
                          <Listbox.Option key={option.id} value={option} className={({ active }) => classNames("flex cursor-pointer items-center justify-between rounded-md px-3 py-2", active ? "bg-slate-100" : "bg-white")}>
                            {({ selected }) => <><span>{option.name}</span>{selected && <CheckIcon className="h-5 w-5 text-[#E3222A]" />}</>}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
                <button type="button" onClick={onFiltersOpen} className="flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  <FilterIcon className="h-5 w-5" />Filters
                  {activeFilterCount > 0 && <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#E3222A] px-1.5 text-xs font-bold text-white">{activeFilterCount}</span>}
                </button>
                <button type="button" onClick={onRefresh} disabled={loading} className="col-span-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 md:col-span-1 md:w-auto">
                  <RefreshIcon className={classNames("h-5 w-5", loading && "animate-spin")} />Update queue
                </button>
              </div>
            </section>

            {activeFilterCount > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-bold uppercase tracking-[0.08em] text-slate-400">Active filters</span>
                {activeFilters.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => onRemoveActiveFilter(filter.id)} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
                    {filter.name}<XIcon className="h-5 w-5" />
                  </button>
                ))}
                {dueToday && <button type="button" onClick={onToggleDueToday} className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-600 ring-1 ring-inset ring-slate-200">Due today ×</button>}
                {overdue && <button type="button" onClick={onToggleOverdue} className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-600 ring-1 ring-inset ring-slate-200">Overdue ×</button>}
                {tags.filter((tag) => tag.selected).map((tag) => <button key={tag.id} type="button" onClick={() => onToggleTag(tag)} className="rounded-full bg-white px-2.5 py-1 font-medium text-slate-600 ring-1 ring-inset ring-slate-200">{tag.name} ×</button>)}
                <button type="button" onClick={onClearAll} className="font-semibold text-[#E3222A] hover:text-red-700">Clear all</button>
              </div>
            )}
          </>
        )}

        <div className={classNames(vendorView ? "mt-5" : "mt-3")}>
          {loading ? <Loader /> : jobs.length ? <QueueList jobs={jobs} vendorView={vendorView} /> : <EmptyState canCreate={canCreate} />}
        </div>

        {!loading && totalJobs > 50 && (
          <div className="flex justify-end py-6">
            <Pagination
              innerClass="pagination pagination-custom"
              activePage={currentPage}
              hideDisabled
              itemClass="page-item page-item-custom"
              linkClass="page-link page-link-custom"
              itemsCountPerPage={50}
              totalItemsCount={totalJobs}
              pageRangeDisplayed={3}
              onChange={onPageChange}
            />
          </div>
        )}
      </div>

      {!vendorView && (
        <FilterDrawer
          open={filtersOpen}
          onClose={onFiltersClose}
          activeFilterCount={activeFilterCount}
          availableStatuses={availableStatuses}
          statusSelected={statusSelected}
          onToggleStatus={onToggleStatus}
          dueToday={dueToday}
          dueTodayCount={dueTodayCount}
          onToggleDueToday={onToggleDueToday}
          overdue={overdue}
          overdueCount={overdueCount}
          onToggleOverdue={onToggleOverdue}
          tags={tags}
          onToggleTag={onToggleTag}
          airportTypes={airportTypes}
          airportTypeSelected={airportTypeSelected}
          onToggleAirportType={onToggleAirportType}
          customers={customers}
          customerSelected={customerSelected}
          onSelectCustomer={onSelectCustomer}
          projectManagers={projectManagers}
          projectManagerSelected={projectManagerSelected}
          onSelectProjectManager={onSelectProjectManager}
          airports={airports}
          airportSelected={airportSelected}
          onSelectAirport={onSelectAirport}
          vendors={vendors}
          vendorSelected={vendorSelected}
          onSelectVendor={onSelectVendor}
          onClearAll={onClearAll}
          resultCount={visibleTotal}
          showVendors={showVendorFilter}
        />
      )}
    </div>
  );
}
